import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { getDocuments, deleteDocument } from '../redux/slices/documentSlice';
import { getSettingValue } from '../redux/slices/appSettingSlice';
import PermissionButton from '../components/common/PermissionButton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DocumentDownloadButtons from '../components/documents/DocumentDownloadButtons';
import { useHasPermission } from '../hooks/usePermission';
import ForbiddenPage from './ForbiddenPage';

/**
 * DocumentListPage - User's document list page
 * 
 * Features:
 * - Search and status filters
 * - Document cards with actions
 * - Permission-based create button
 * - Status badges (draft, completed, signed, shared)
 * 
 * Phase 6C: Document Management
 */
const DocumentListPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { documents, loading, error } = useSelector(
    (state) => state.documents
  );
  const { settingValues } = useSelector(
    (state) => state.appSettings
  );

  const hasPermission = useHasPermission('documents.view');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  const [draftLimitDialog, setDraftLimitDialog] = useState({ open: false, maxDrafts: 5 });

  // Debounce search term (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (hasPermission) {
      const params = {
        status: statusFilter || undefined,
        page: page + 1,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      dispatch(getDocuments(params));
    }
  }, [dispatch, statusFilter, hasPermission, page, limit]);

  // Filter documents by search term (client-side for now)
  const filteredDocuments = documents.filter((doc) => {
    if (!debouncedSearch) return true;
    const searchLower = debouncedSearch.toLowerCase();
    const templateTitle = doc.templateTitle || doc.template?.title || '';
    return (
      doc.title?.toLowerCase().includes(searchLower) ||
      templateTitle.toLowerCase().includes(searchLower)
    );
  });

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status filter
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  // Helper function to get templateId (handles both object and string)
  const getTemplateId = (templateId) => {
    if (!templateId) return null;
    return typeof templateId === 'object' && templateId?._id ? templateId._id : templateId;
  };

  // Handle back
  const handleBack = () => {
    history.goBack();
  };

  const handleDocumentClick = (document) => {
    if (document.status === 'draft') {
      const templateId = getTemplateId(document.templateId);
      if (templateId) {
        history.push(`/documents/wizard/${templateId}?documentId=${document._id}`);
      }
    } else {
      history.push(`/documents/${document._id}`);
    }
  };

  const handleEdit = (document) => {
    const templateId = getTemplateId(document.templateId);
    if (templateId) {
      history.push(`/documents/wizard/${templateId}?documentId=${document._id}`);
    }
  };

  const handleView = (document) => {
    history.push(`/documents/${document._id}`);
  };

  const handleDownload = (document) => {
    if (document.pdfUrl) {
      window.open(document.pdfUrl, '_blank');
    }
  };

  const handleDeleteClick = (document) => {
    setDeleteDialog({ open: true, document });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, document: null });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.document) {
      try {
        await dispatch(deleteDocument(deleteDialog.document._id)).unwrap();
        setDeleteDialog({ open: false, document: null });
        // Liste yenileme
        const params = {
          status: statusFilter || undefined,
          page: page + 1,
          limit,
        };
        Object.keys(params).forEach((key) => {
          if (params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });
        dispatch(getDocuments(params));
      } catch (error) {
        console.error('Doküman silinirken hata oluştu:', error);
      }
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateDocument = async () => {
    // Check draft count limit
    try {
      // Get max_draft_documents setting (from cache or fetch)
      let maxDrafts = 5; // Default value
      
      if (settingValues.max_draft_documents) {
        // Use cached value
        maxDrafts = parseInt(settingValues.max_draft_documents, 10);
      } else {
        // Fetch from API
        try {
          const result = await dispatch(getSettingValue('max_draft_documents')).unwrap();
          maxDrafts = parseInt(result?.value || '5', 10);
        } catch (error) {
          console.error('Error fetching max_draft_documents:', error);
          // Use default value
        }
      }
      
      // Count current draft documents
      const draftCount = documents.filter(doc => doc.status === 'draft').length;
      
      if (draftCount >= maxDrafts) {
        // Show draft limit dialog
        setDraftLimitDialog({ open: true, maxDrafts });
        return;
      }
      
      // If under limit, proceed to create document
      history.push('/documents/new');
    } catch (error) {
      console.error('Error checking draft limit:', error);
      // If error, proceed anyway (fallback)
      history.push('/documents/new');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'completed':
        return 'success';
      case 'signed':
        return 'primary';
      case 'shared':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft':
        return 'Taslak';
      case 'completed':
        return 'Tamamlandı';
      case 'signed':
        return 'İmzalandı';
      case 'shared':
        return 'Paylaşıldı';
      default:
        return status;
    }
  };

  // Permission-based guard check
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Dokümanlarım
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Oluşturduğunuz ve paylaşılan dokümanları buradan yönetebilirsiniz
          </Typography>
        </Box>
        <PermissionButton
          permission="documents.create"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateDocument}
        >
          Yeni Doküman
        </PermissionButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || error}
        </Alert>
      )}

      {/* Filters (in Paper) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            placeholder="Doküman adı veya şablon adı ile ara..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ flex: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Status Filter */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              label="Durum"
              onChange={handleStatusFilter}
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="draft">Taslak</MenuItem>
              <MenuItem value="completed">Tamamlandı</MenuItem>
              <MenuItem value="signed">İmzalandı</MenuItem>
              <MenuItem value="shared">Paylaşıldı</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading && filteredDocuments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Dokümanlar yükleniyor...
          </Typography>
        </Paper>
      ) : !loading && filteredDocuments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {debouncedSearch || statusFilter ? 'Doküman bulunamadı' : 'Henüz doküman oluşturmadınız'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {!debouncedSearch && !statusFilter && 'Yeni bir doküman oluşturmak için "Yeni Doküman" butonuna tıklayın'}
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Başlık</TableCell>
                  <TableCell>Şablon</TableCell>
                  <TableCell>Oluşturan</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((document) => (
                  <TableRow key={document._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleDocumentClick(document)}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {document.title || 'Başlıksız Doküman'}
                        </Typography>
                        {document.tags && document.tags.length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {document.tags.slice(0, 2).map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: '20px' }}
                              />
                            ))}
        </Box>
      )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {document.templateTitle || document.template?.title || 'Bilinmeyen Şablon'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {document.userId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {typeof document.userId === 'object' && document.userId.firstName
                              ? `${document.userId.firstName} ${document.userId.lastName || ''}`.trim()
                              : 'Bilinmiyor'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={getStatusLabel(document.status)}
                        color={getStatusColor(document.status)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {document.updatedAt
                          ? format(new Date(document.updatedAt), 'dd MMM yyyy', { locale: tr })
                          : 'Tarih bilgisi yok'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                      {document.updatedAt
                          ? format(new Date(document.updatedAt), 'HH:mm:ss', { locale: tr })
                          : ''}
                    </Typography>
                    </TableCell>

                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {document.status === 'draft' ? (
                          <>
                      <Tooltip title="Düzenle">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                                  handleEdit(document);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(document);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                      <Tooltip title="Görüntüle">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                                handleView(document);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {document.status === 'completed' && (
                        <DocumentDownloadButtons
                          documentId={document._id || document.id}
                          documentTitle={document.title}
                          status={document.status}
                        />
                      )}
                          </>
                    )}
                    {document.pdfUrl && (
                      <Tooltip title="PDF İndir">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                                handleDownload(document);
                          }}
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                      </Box>
                    </TableCell>
                  </TableRow>
            ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredDocuments.length > 0 && (
            <TablePagination
              component="div"
              count={filteredDocuments.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Sayfa başına:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        title="Dokümanı Sil"
        message={`"${deleteDialog.document?.title || 'Bu doküman'}" dokümanını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
        severity="error"
      />

      {/* Draft Limit Dialog */}
      <Dialog
        open={draftLimitDialog.open}
        onClose={() => setDraftLimitDialog({ open: false, maxDrafts: 5 })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Maksimum Taslak Sayısına Ulaşıldı
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Maksimum {draftLimitDialog.maxDrafts} taslak doküman oluşturabilirsiniz. Lütfen mevcut taslak dokümanlarınızı tamamlayın veya silin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDraftLimitDialog({ open: false, maxDrafts: 5 });
              // Filter to show only drafts
              setStatusFilter('draft');
            }}
            variant="contained"
            color="primary"
            autoFocus
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DocumentListPage;

